import React, { useState, useEffect, useRef } from "react";
import { ReactComponent as ArrowDownIcon } from "../../../Assets/svg/arrow-down.svg";
import { ReactComponent as ArrowUpIcon } from "../../../Assets/svg/arrow-up.svg";
import { getApi } from "../api-client.js";

const MultiSelectDropDown = ({
  searchId = -1,
  onDropdownChange,
  apiUrl,
  placeholder,
  selectedValue,
  selectedOption,
  params = {},
  staticOptions = [],
  placeholderColor = "#81878C",
  customHeaderStyle = {},
  customArrow = null,
  customWrapperStyle = {},
  width = "100%",
  // When set, show at most this many selected labels in the header followed by
  // a "+N more" count. Defaults to 2 so multiple selections remain clearly visible.
  maxVisibleLabels = 2,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [options, setOptions] = useState([]);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const dropdownRef = useRef(null);
  const [openDirection, setOpenDirection] = useState("down");

  const getOptionValue = (item) => {
    if (item === null || item === undefined) return "";
    if (typeof item !== "object") return String(item);
    return String(
      item.value ??
      item.id ??
      item.uuid ??
      item.team_id ??
      item.project_id ??
      item.component_id ??
      item.emp_id ??
      item.user_id ??
      item.code ??
      ""
    );
  };

  const getOptionLabel = (item) => {
    if (item === null || item === undefined) return "";
    if (typeof item !== "object") return String(item);
    return String(
      item.label ??
      item.name ??
      item.title ??
      item.team_name ??
      item.project_name ??
      item.component_name ??
      item.emp_name ??
      item.team ??
      item.text ??
      getOptionValue(item)
    );
  };

  const isOptionSelected = (opt) => {
    if (!selectedOption || !Array.isArray(selectedOption) || !opt) return false;
    const optVal = getOptionValue(opt);
    if (!optVal) return false;

    return selectedOption.some((s) => {
      const sVal = getOptionValue(s);
      return sVal !== "" && sVal === optVal;
    });
  };

  const fetchOptions = async () => {
    setLoading(true);
    setError(null);

    try {
      if (apiUrl) {
        const endpoint = apiUrl.split("?")[0];

        const res = await getApi(endpoint, {
          ...(params ?? {}),
          term: searchTerm,
          search: searchTerm,
        });

        if (res?.success === true) {
          const rawList = res.suggestionlist || res.suggestion_list || res.data || res.locations || res.location || [];
          const list = rawList.map((item) => {
            const val = getOptionValue(item);
            const lbl = getOptionLabel(item);
            return typeof item === "object" && item !== null
              ? { ...item, value: val, label: lbl }
              : { value: val, label: lbl };
          });
          setOptions(list);
          setFilteredOptions(list);
        } else {
          setOptions([]);
          setFilteredOptions([]);
        }
      } else {
        // Local search fallback
        if (searchTerm !== "") {
          setFilteredOptions(
            options.filter((option) =>
              getOptionLabel(option)
                .toLowerCase()
                .includes(searchTerm.toLowerCase())
            )
          );
        } else {
          setFilteredOptions(options);
        }
      }
    } catch (err) {
      console.error("Error fetching options:", err);
      setError("No options found.");
      setOptions([]);
      setFilteredOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const serializedParams = JSON.stringify(params);

  // Fetch options when component mounts, search term changes, apiUrl changes, or params change
  useEffect(() => {
    fetchOptions();
  }, [searchTerm, apiUrl, serializedParams]);

  // Filter options based on search term
  useEffect(() => {
    if (searchTerm === "") {
      setFilteredOptions(options);
    } else {
      setFilteredOptions(
        options.filter((option) =>
          getOptionLabel(option).toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }
  }, [searchTerm, options]);

  // Handle outside click to close the dropdown
  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const dropdownHeight = 280; // max-height (250px) + search box & padding
      if (spaceBelow < dropdownHeight && rect.top > dropdownHeight) {
        setOpenDirection("up");
      } else {
        setOpenDirection("down");
      }
    }
  }, [isOpen]);

  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
    if (!isOpen) {
      if (apiUrl === undefined || apiUrl === "") {
        setOptions(staticOptions);
      }
      fetchOptions();
    }
  };

  const selectOption = (option) => {
    if (!option) return;
    const optVal = getOptionValue(option);
    const optLabel = getOptionLabel(option);
    const normalizedOption = {
      ...(typeof option === "object" ? option : {}),
      value: optVal,
      label: optLabel,
    };
    if (searchId === -1) {
      onDropdownChange(normalizedOption);
    } else {
      onDropdownChange(normalizedOption, searchId);
    }
  };

  return (
    <div
      ref={dropdownRef}
      style={{
        position: "relative",
        height: "40px",
        width: width,
        zIndex: isOpen ? 1000 : 1,
        ...customWrapperStyle,
      }}
    >
      {/* Dropdown Header */}
      <div
        onClick={toggleDropdown}
        style={{
          height: "40px",
          padding: "10px",
          border: isOpen ? "1px solid #6929E2" : "1px solid #E5E5E5",
          borderRadius: "8px",
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: "#FFFFFF",
          color: selectedOption && selectedOption.length > 0 ? "#1E1E1E" : placeholderColor,
          ...customHeaderStyle,
        }}
      >
        <span
          style={{
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            overflow: "hidden",
            flexShrink: 1,
            display: "flex",
            alignItems: "center",
          }}
        >
          {(() => {
            if (!Array.isArray(selectedOption)) return placeholder;
            const selected = selectedOption.filter((item) => {
              if (!item) return false;
              const val = typeof item === "object" ? (item.value ?? item.id ?? "") : item;
              return val !== "" && val !== null && val !== undefined;
            });
            if (selected.length === 0) return placeholder;
            const getLabel = (item) => (typeof item === "object" ? (item.label ?? item.name ?? item.value ?? "") : item);
            if (maxVisibleLabels == null || selected.length <= maxVisibleLabels) {
              return selected.map(getLabel).join(", ");
            }
            const shown = selected.slice(0, maxVisibleLabels).map(getLabel).join(", ");
            const remaining = selected.length - maxVisibleLabels;
            return (
              <span style={{ display: "inline-flex", alignItems: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{shown}</span>
                <span style={{ color: "#6929E2", background: "#F5F2FD", borderRadius: "100px", padding: "1px 6px", fontSize: "11px", fontWeight: 700, marginLeft: "6px", flexShrink: 0 }}>+{remaining} more</span>
              </span>
            );
          })()}
        </span>
        <span style={{ marginLeft: customHeaderStyle.gap || "10px", flexShrink: 0 }}>
          {customArrow ? customArrow : (isOpen ? <ArrowUpIcon /> : <ArrowDownIcon />)}
        </span>
      </div>
      {/* Dropdown Options */}
      {isOpen && (
        <div
          style={{
            position: "absolute",
            ...(openDirection === "up"
              ? { bottom: "100%", marginBottom: "4px" }
              : { top: "100%", marginTop: "4px" }),
            left: 0,
            border: "1px solid #E5E5E5",
            borderRadius: "12px",
            background: "#FFFFFF",
            zIndex: 2000,
            maxHeight: "250px",
            overflowY: "auto",
            overflowX: "hidden",
            width: "100%",
            boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
          }}
        >
          {/* Search Input */}
          <div style={{ padding: "8px", position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search..."
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #E5E5E5",
                borderRadius: "8px",
                boxSizing: "border-box",
                color: "#1E1E1E",
                fontSize: "13px",
                fontFamily: "Satoshi",
                outline: "none",
              }}
            />
          </div>

          {/* Options */}
          <div style={{ padding: "4px" }}>
            {loading ? (
              <div style={{ padding: "12px", textAlign: "center", fontSize: "13px", color: "#8790A1", fontFamily: "Satoshi" }}>
                Loading...
              </div>
            ) : error ? (
              <div style={{ padding: "12px", textAlign: "center", color: "#FF4444", fontSize: "13px", fontFamily: "Satoshi" }}>
                {error}
              </div>
            ) : filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => {
                const selected = isOptionSelected(option);
                return (
                  <div
                    key={index}
                    onClick={() => selectOption(option)}
                    style={{
                      padding: "8px 12px",
                      cursor: "pointer",
                      background: selected ? "#F5F2FD" : "transparent",
                      color: selected ? "#6929E2" : "#212129",
                      textAlign: "left",
                      fontSize: "14px",
                      fontFamily: "Satoshi",
                      fontWeight: selected ? "600" : "500",
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      transition: "all 0.2s ease",
                      userSelect: "none",
                    }}
                    onMouseEnter={(e) => {
                      if (!selected) {
                        e.currentTarget.style.background = "#F9F9FB";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!selected) {
                        e.currentTarget.style.background = "transparent";
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      readOnly
                      style={{
                        accentColor: "#6929E2",
                        width: "16px",
                        height: "16px",
                        cursor: "pointer",
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {option.label}
                    </span>
                  </div>
                );
              })
            ) : (
              <div style={{ padding: "12px", textAlign: "center", fontSize: "13px", color: "#8790A1", fontFamily: "Satoshi" }}>
                No records found.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropDown;
